<template>
  <div class="me-page">

    <!-- Loading skeleton — aceeași structură ca layoutul final, ca pagina să nu sară -->
    <div v-if="loadingProfile" class="me-grid">
      <div class="me-main">
        <v-card flat class="me-card">
          <v-skeleton-loader type="heading, paragraph" />
        </v-card>
        <v-card flat class="me-card">
          <v-skeleton-loader type="heading, button" />
        </v-card>
        <v-card flat class="me-card">
          <v-skeleton-loader type="heading, paragraph" />
        </v-card>
        <v-card flat class="me-card">
          <v-skeleton-loader type="heading, paragraph" />
        </v-card>
      </div>
      <div class="me-rail">
        <v-card flat class="me-card">
          <v-skeleton-loader type="avatar, heading, list-item-two-line" />
        </v-card>
      </div>
    </div>

    <template v-else>
      <!-- Profile error -->
      <v-alert
        v-if="profileError"
        type="error"
        class="mb-4"
      >
        {{ profileError }}
      </v-alert>

      <div class="me-grid">

        <!-- ── Main settings column — 3 rânduri de carduri ────── -->
        <div class="me-main">

          <!-- Rândul 1 · Limbă -->
          <v-card flat class="me-card">
            <div class="me-section-header">
              <v-icon size="18" class="me-section-header__icon">mdi-translate</v-icon>
              <span>{{ $t("settings.profile.language.label") }}</span>
            </div>
            <p class="me-section-helper">{{ $t("settings.profile.language.helper") }}</p>
            <div class="me-lang-grid">
              <button
                v-for="loc in localeOptions"
                :key="loc.code"
                type="button"
                class="me-lang-btn"
                :class="{ 'me-lang-btn--active': selectedLocale === loc.code }"
                @click="onLocaleChange(loc.code)"
              >
                <span class="me-lang-btn__flag">{{ localeFlags[loc.code] }}</span>
                <span class="me-lang-btn__label">{{ loc.label }}</span>
                <v-icon
                  size="18"
                  class="me-lang-btn__check"
                  :style="{ opacity: selectedLocale === loc.code ? 1 : 0 }"
                >mdi-check-circle</v-icon>
              </button>
            </div>
          </v-card>

          <!-- Rândul 1 · Parolă -->
          <v-card flat class="me-card">
            <div class="me-section-header">
              <v-icon size="18" class="me-section-header__icon">mdi-lock-outline</v-icon>
              <span>{{ $t("settings.profile.password.title") }}</span>
            </div>
            <div class="me-security">
              <p class="me-section-helper me-security__text">
                {{ $t("settings.profile.password.helper") }}
              </p>
              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-lock-reset"
                class="me-btn"
                @click="openChangePasswordDialog"
              >
                {{ $t("settings.profile.password.change") }}
              </v-btn>
            </div>
          </v-card>

          <!-- Rândul 2 · Telegram -->
          <v-card flat class="me-card">
            <TelegramUserLinkPanel />
          </v-card>

          <!-- Rândul 2 · Adrese email personale -->
          <v-card flat class="me-card">
            <UserEmailAccountsPanel
              :user-email="currentUser.email"
              :user-name="currentUser.name"
            />
          </v-card>

          <!-- Rândul 3 · Identitate Google, pe toată lățimea coloanei -->
          <div class="me-main__wide">
            <slot name="google" />
          </div>

        </div>

        <!-- ── Identity rail ─────────────────────────────────── -->
        <aside class="me-rail">
          <div class="me-rail__sticky">
            <UserIdentityCard
              class="me-card me-card--flush"
              :user="currentUser"
              :initials="initials"
              :meta-rows="metaRows"
            />
            <UserRolesCard
              class="me-card"
              :role-keys="allRoleKeys"
              :primary-role="currentUser.role"
              :user-id="currentUser.id"
            />
          </div>
        </aside>

      </div>
    </template>

    <UserAccountPasswordDialog v-model="changePasswordDialog" :user-id="currentUser.id" />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from "vue";
import axios from "axios";
import { computeInitials } from "@/components/userAccount/utils.mjs";
import { useI18n } from "vue-i18n";
import { ensureSupportedLocale, persistLocale } from "@/Utile/i18n.js";
import { availableLocales } from "@/plugins/i18n.js";
import TelegramUserLinkPanel from "@/components/integrations/telegram/TelegramUserLinkPanel.vue";
import UserEmailAccountsPanel from "@/components/userAccount/UserEmailAccountsPanel.vue";
import UserAccountPasswordDialog from "@/components/userAccount/UserAccountPasswordDialog.vue";
import UserIdentityCard from "@/components/userAccount/UserIdentityCard.vue";
import UserRolesCard from "@/components/userAccount/UserRolesCard.vue";

// dd.mm.yyyy — per design.md, user-facing dates never follow browser locale.
const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

export default defineComponent({
  name: "UserAccountSettings",
  components: {
    TelegramUserLinkPanel,
    UserEmailAccountsPanel,
    UserAccountPasswordDialog,
    UserIdentityCard,
    UserRolesCard,
  },
  emits: ["profile-loaded"],
  setup(props, { emit }) {
    const { t, locale } = useI18n({ useScope: "global" });
    const loadingProfile = ref(false);
    const profileError = ref("");
    const currentUser = ref({
      id: null,
      name: "",
      email: "",
      role: "",
      roles: [],
      companyName: "",
      createdAt: null,
    });

    const changePasswordDialog = ref(false);
    const selectedLocale = ref(ensureSupportedLocale(locale.value));
    const localeOptions = availableLocales;
    const localeFlags = { ro: "🇷🇴", en: "🇬🇧", ru: "🇷🇺" };

    const initials = computed(() => computeInitials(currentUser.value?.name || ""));
    const allRoleKeys = computed(() => {
      const roles = Array.isArray(currentUser.value?.roles) ? currentUser.value.roles : [];
      const ordered = [];
      const seen = new Set();
      roles.forEach((entry) => {
        const rawKey = entry?.key || entry?.role_key || entry?.role || entry?.name || "";
        const key = String(rawKey || "").trim().toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        ordered.push(key);
      });
      return ordered;
    });

    const metaRows = computed(() => {
      const rows = [];
      const memberSince = formatDate(currentUser.value?.createdAt);
      if (memberSince) {
        rows.push({
          icon: "mdi-calendar-outline",
          label: t("settings.profile.meta.memberSince"),
          value: memberSince,
        });
      }
      if (currentUser.value?.companyName) {
        rows.push({
          icon: "mdi-domain",
          label: t("settings.profile.meta.company"),
          value: currentUser.value.companyName,
        });
      }
      return rows;
    });

    const fetchProfile = async () => {
      loadingProfile.value = true;
      profileError.value = "";
      try {
        const { data } = await axios.get("/me");
        currentUser.value = {
          id: data?.id ?? null,
          name: data?.name || "",
          email: data?.email || "",
          role: data?.role || "",
          roles: Array.isArray(data?.roles) ? data.roles : [],
          companyName: data?.company_name || "",
          createdAt: data?.created_at || null,
        };
        emit("profile-loaded", currentUser.value);
      } catch (error) {
        console.error("Eroare la obținerea profilului utilizatorului:", error);
        profileError.value = t("settings.profile.messages.profileError");
      } finally {
        loadingProfile.value = false;
      }
    };

    const openChangePasswordDialog = () => {
      changePasswordDialog.value = true;
    };

    const onLocaleChange = (newLocale) => {
      const normalized = ensureSupportedLocale(newLocale);
      selectedLocale.value = normalized;
      locale.value = normalized;
      persistLocale(normalized);
    };

    onMounted(() => {
      fetchProfile();
    });

    return {
      t,
      localeOptions,
      localeFlags,
      selectedLocale,
      onLocaleChange,
      loadingProfile,
      profileError,
      currentUser,
      changePasswordDialog,
      initials,
      allRoleKeys,
      metaRows,
      openChangePasswordDialog,
    };
  },
});
</script>

<style scoped src="./UserAccountSettings.styles.css"></style>
